import * as yup from 'yup';
import { AnySchema } from 'yup';
import { ApiTypesEnum } from '@api/ringil-api.v2.tsx';
import { LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { createOrdersComponentValidationSchema, OrdersConfig } from '@components/framework/orders/validation.ts';
import { timeslotValidationSchema } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import { ladenFactorValueValidation } from '@utils/expeditionUtils.ts';
import { CargoTypeCodeEnum } from '@utils/constants.ts';
import dayjs from 'dayjs';
import { missingMuPrices, resolveContractedPrices } from '@utils/contractedPriceTable.ts';
import { muCodesOf, unitsByMuFromLocations } from '@app/pages/expedition/create/contractedUnits.ts';
import type { ContractedCarrierForm } from '@components/ringil3/Features/Contracted/types.ts';
import {
  CarrierDistributionSelectorData,
  contractedMixedMuMessage,
  resolveResponseDeadline,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';

type Translate = (key: string, defaultText?: string, data?: any, count?: number) => string;

export interface ExpeditionValidationDeps {
  tr: Translate;
  /** Orders config from `expeditionConfig(...)` — decides which cargo dimensions are mandatory. */
  ordersConfig: OrdersConfig;
  ladenFactorEnabled: boolean;
  /** RD-2977: expedition module setting `folders.required` — at least one folder must be chosen. */
  folderRequired?: boolean;
  /** Timezone of a relative offer deadline - its day picks the contracted price level. */
  defaultTimezoneId?: string;
}

const additionalPersonsValidation = (tr: Translate) =>
  yup.array().of(
    yup.object().shape({
      email: yup.string().email(tr(`validation.valid_email`, 'Value has to be valid email address.')).required(),
    }),
  );

/** Types with an offer deadline: the carrier answers within it. */
const hasOfferDeadline = (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'CONTRACTED';

/**
 * The CONTRACTED part of the distribution, as the backend prices it: at least one carrier; the plan has a level on the
 * deadline day; every carrier prices every MU of the shipment on that level (exact laden factor row, else Výchozí).
 */
export const contractedCarriersValidation = (tr: Translate, muCodes: string[] | (() => string[]), defaultTimezoneId: string) =>
  yup.array().when('distributionType', {
    is: 'CONTRACTED',
    then: yup
      .array()
      .min(1, tr('CreateExpedition.mandatoryContractedCarriers', 'Přidejte alespoň jednoho smluvního dopravce'))
      .required(tr('CreateExpedition.mandatoryContractedCarriers', 'Přidejte alespoň jednoho smluvního dopravce'))
      .test('contracted-pricing', '', function (value) {
        const distribution = this.parent as CarrierDistributionSelectorData;
        const codes = typeof muCodes === 'function' ? muCodes() : muCodes;
        const issue = contractedPricingIssue(tr, distribution, (value ?? []) as ContractedCarrierForm[], codes, defaultTimezoneId);
        return issue ? this.createError({ message: issue }) : true;
      }),
    otherwise: yup.array().nullable(),
  });

/** First reason the backend would refuse to price a CONTRACTED distribution, as a message; null = fine. */
export const contractedPricingIssue = (
  tr: Translate,
  distribution: CarrierDistributionSelectorData,
  carriers: ContractedCarrierForm[],
  muCodes: string[],
  defaultTimezoneId: string,
): string | null => {
  const table = distribution?.contractedPriceTable;
  if (!table || table.rows.length === 0) {
    return tr('CreateExpedition.contractedTableRequired', 'Nastavte tabulku smluvních cen (manipulační jednotky a ladenfaktory)');
  }
  const deadline = resolveResponseDeadline(distribution, defaultTimezoneId);
  const deadlineDate = deadline.localTime.tz(deadline.timezone).format('YYYY-MM-DD');
  const shipmentLf = distribution.ladenFactor ? distribution.ladenFactorValue : null;
  const resolved = resolveContractedPrices(table, carriers, deadlineDate, shipmentLf, muCodes);
  if (!resolved.level) {
    return tr('CreateExpedition.contractedNoLevel', 'Na den uzávěrky poptávky {{date}} není naplánovaná cenová úroveň', { date: dayjs(deadlineDate).format('D. M. YYYY') });
  }
  const muName = (mu: string) => table.rows.find(r => r.cargoTemplateCode === mu)?.cargoTemplateName || mu;
  const missing = carriers
    .map(c => ({ carrier: c, mus: missingMuPrices(resolved, c.carrierId) }))
    .filter(m => m.mus.length > 0);
  if (missing.length > 0) {
    return tr('CreateExpedition.contractedMissingMuPrice', 'Dopravce {{carrier}} nemá pro {{level}} cenu za {{mu}}', {
      carrier: missing[0].carrier.carrierName,
      level: resolved.level.name,
      mu: missing[0].mus.map(muName).join(', '),
    });
  }
  return null;
};

const manuallyEnteredLocationValidation = (tr: Translate) =>
  yup.object().shape({
    name: yup.string().required(),
    city: yup.string().required(),
    streetNr: yup.string().required(),
    countryCode3: yup.string().required(),
    postalCode: yup.string().required(),
    timezoneId: yup.string().required(tr('validation.required', 'This field is required')),
    mainContactEmail: yup.string().nullable().email(tr(`validation.valid_email`, 'Value has to be valid email address.')),
    additionalPersons: additionalPersonsValidation(tr),
  });

/**
 * Validation for the create-shipment form.
 *
 * Returned as a *factory* rather than a schema: `useForm` hands the data being validated to the
 * factory, which is what the Dachser order-description rule needs — its limit depends on which
 * carriers are selected, i.e. on another part of the same form.
 */
export const buildExpeditionValidationSchema =
  ({ tr, ordersConfig, ladenFactorEnabled, folderRequired = false, defaultTimezoneId }: ExpeditionValidationDeps) =>
    (values: NewExpeditionForm): AnySchema => {
      const orderSchema = createOrdersComponentValidationSchema(ordersConfig, false, tr).shape({
        orderDescription: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            'dachser-length',
            tr('CreateExpedition.dachserOrderDescriptionTooLong', 'Order description must be max 20 characters for Dachser'),
            function(value) {
              if (!value) return true;

              const hasDachser = values?.distribution?.selectedItems?.some(item => item.apiTypes?.includes(ApiTypesEnum.DACHSER));

              return !(hasDachser && value.length > 20);
            },
          ),
      });

      const contractedMuCodes = muCodesOf(unitsByMuFromLocations(values?.locations ?? []));

      return yup.object().shape({
        locations: yup.array().of(
          yup.object().shape({
            localityMode: yup.string().required(),
            timezoneId: yup.string().required(tr('validation.required', 'This field is required')),
            timeslots: timeslotValidationSchema(),
            manualAddress: yup.object().when('localityMode', {
              is: LocalityEntryModeEnum.MANUALLY,
              then: manuallyEnteredLocationValidation(tr),
              otherwise: yup.object().nullable(),
            }),
            additionalPersons: additionalPersonsValidation(tr).nullable(),
            selectedPerson: yup.object().nullable(),
            companyLocationId: yup
              .string()
              .when('localityMode', {
                is: LocalityEntryModeEnum.FROM_LOCATIONS,
                then: yup.string().required().min(1),
                otherwise: yup.string().nullable(),
              })
              // A stop limited by a Linka must use one of the line's candidate locations.
              .test('limited-location', tr('CreateExpedition.limitedLocationRequired', 'Vyberte jednu z lokalit linky'), function (value) {
                const { locationLock, allowedLocationIds } = this.parent ?? {};
                if (locationLock !== 'LIMITED' || !value) return true;
                return (allowedLocationIds ?? []).includes(value);
              }),
            orders: yup.array().nullable().min(0).of(orderSchema),
          }),
        ),

        distribution: yup
          .object()
          .shape({
            distributionType: yup.string(),
            price: yup.object().when('distributionType', {
              // Any distribution type at all, except CONTRACTED: its price comes from the contract.
              is: (type: string) => !!type && type !== 'CONTRACTED',
              then: yup
                .object()
                .shape({
                  price: yup
                    .number()
                    .typeError(tr('CreateExpedition.priceShouldBeNumber', 'Price should be number'))
                    .transform(value => (isNaN(value) ? undefined : value))
                    .nullable()
                    .required(tr('CreateExpedition.mandatoryPrice', 'Price is required')),
                  currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
                })
                .required(tr('CreateExpedition.mandatoryPrice', 'Price is required')),
              otherwise: yup.object().nullable(),
            }),
            selectedItems: yup.array().when('distributionType', {
              is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
              then: yup
                .array()
                .min(1, tr('CreateExpedition.mandatoryCarriers', 'Please select at least one carrier'))
                .required(tr('CreateExpedition.mandatoryCarriers', 'Please select at least one carrier'))
                // FedEx Express Freight needs the booking confirmation number from FedEx Freight Customer Service
                .test(
                  'fedex-freight-booking',
                  tr('FedexProductTerms.bookingConfirmationRequired', 'FedEx booking confirmation number is required for freight services'),
                  items => !items?.some(item => item._fedexProductTypeCode?.includes('_FREIGHT') && !item._fedexBookingConfirmationNumber?.trim()),
                )
                // several FedEx accounts allowed for the route - the user has to pick one
                .test(
                  'fedex-account',
                  tr('FedexProductTerms.accountRequired', 'Select the FedEx account the shipment goes on'),
                  items => !items?.some(item => item._fedexAccountRequired && !item._fedexAccountId),
                ),
              otherwise: yup.array().nullable(),
            }),
            contractedCarriers: contractedCarriersValidation(tr, contractedMuCodes, defaultTimezoneId ?? 'Europe/Prague'),
            responseDeadlineOffsetValue: yup.number().when(['distributionType', 'responseDeadlineMode'], {
              is: (type: string, mode: string) => hasOfferDeadline(type) && mode !== 'absolute',
              then: yup
                .number()
                .typeError(tr('InputDeadline.required', 'Offer deadline is required'))
                .min(1, tr('InputDeadline.required', 'Offer deadline is required'))
                .required(tr('InputDeadline.required', 'Offer deadline is required')),
              otherwise: yup.number().nullable(),
            }),
            responseDeadlineLocalTime: yup.mixed().when(['distributionType', 'responseDeadlineMode'], {
              is: (type: string, mode: string) => hasOfferDeadline(type) && mode === 'absolute',
              then: yup.mixed().required(tr('InputDeadline.required', 'Offer deadline is required')),
              otherwise: yup.mixed().nullable(),
            }),
            ladenFactorValue: ladenFactorValueValidation(
              {
                shouldBeNumber: tr('CreateExpedition.ladenFactorShouldBeNumber', 'Laden factor should be a number'),
                required: tr('CreateExpedition.mandatoryLadenFactor', 'Laden factor is required'),
              },
              ladenFactorEnabled,
              hasOfferDeadline,
            ).test('contracted-single-mu', contractedMixedMuMessage(tr), function () {
              // With a laden factor the carrier takes units, which only adds up for a single manipulation unit.
              const { distributionType, ladenFactor } = this.parent ?? {};
              return !(ladenFactorEnabled && distributionType === 'CONTRACTED' && ladenFactor === true && contractedMuCodes.length > 1);
            }),
          })
          .nullable(),

        // Shipment type is optional (RD-2961): an empty value is saved as "no type".
        expeditionType: yup.string().nullable(),

        // RD-2977: the company can require every shipment to be filed in at least one folder.
        folders: folderRequired
          ? yup.array().min(1, tr('CreateExpedition.mandatoryFolder', 'Folder is required')).required(tr('CreateExpedition.mandatoryFolder', 'Folder is required'))
          : yup.array().nullable(),

        // The shipment parameters that start out pre-filled with a default. Emptying one — which is
        // what a merge of shipments that disagreed does — has to be an error rather than an opening
        // for the backend to put a value back in that reflects nothing the user chose.

        cargoType: yup
          .object()
          .nullable()
          .required(tr('CreateExpedition.mandatoryCargoType', 'Cargo type is required'))
          .shape({
            cargoTypeCode: yup.string().nullable().required(tr('CreateExpedition.mandatoryCargoType', 'Cargo type is required')),
            adrWeight: yup.number().when('cargoTypeCode', {
              is: CargoTypeCodeEnum.ADR_LQ,
              then: schema => schema.required(),
              otherwise: schema => schema.notRequired().nullable(),
            }),
          }),
      });
    };